/**
 * useDeepLinking Hook
 *
 * React hook for URL <-> Modal state synchronization:
 * - Sync modal open/close to ?method=ID URL parameter
 * - History API: pushState/popState
 * - Browser back button closes modal
 * - Browser forward button reopens modal
 */

import { useState, useEffect } from 'react';

interface UseDeepLinkingReturn {
  selectedMethodId: number | null;
  setSelectedMethodId: (id: number | null) => void;
  isModalOpen: boolean;
  setIsModalOpen: (open: boolean) => void;
}

export const useDeepLinking = (): UseDeepLinkingReturn => {
  const [selectedMethodId, setSelectedMethodIdState] = useState<number | null>(
    null
  );
  const [isModalOpen, setIsModalOpenState] = useState<boolean>(false);

  // Parse URL on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const methodParam = params.get('method');

    if (methodParam) {
      const methodId = parseInt(methodParam, 10);
      if (!isNaN(methodId)) {
        setSelectedMethodIdState(methodId);
        setIsModalOpenState(true);
      }
    }
  }, []);

  // Update URL when selectedMethodId changes
  useEffect(() => {
    if (selectedMethodId !== null && isModalOpen) {
      const url = new URL(window.location.href);
      url.searchParams.set('method', selectedMethodId.toString());
      window.history.pushState(
        { modalOpen: true, methodId: selectedMethodId },
        '',
        url.toString()
      );
    } else if (selectedMethodId === null && !isModalOpen) {
      const url = new URL(window.location.href);
      url.searchParams.delete('method');
      window.history.pushState({ modalOpen: false }, '', url.toString());
    }
  }, [selectedMethodId, isModalOpen]);

  // Listen to popstate (back/forward buttons)
  useEffect(() => {
    const handlePopState = (event: PopStateEvent): void => {
      const params = new URLSearchParams(window.location.search);
      const methodParam = params.get('method');

      if (methodParam) {
        const methodId = parseInt(methodParam, 10);
        if (!isNaN(methodId)) {
          setSelectedMethodIdState(methodId);
          setIsModalOpenState(true);
        }
      } else {
        setSelectedMethodIdState(null);
        setIsModalOpenState(false);
      }
    };

    window.addEventListener('popstate', handlePopState);

    return (): void => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Wrapper functions to sync both state and URL
  const setSelectedMethodId = (id: number | null): void => {
    setSelectedMethodIdState(id);
    if (id !== null) {
      setIsModalOpenState(true);
    }
  };

  const setIsModalOpen = (open: boolean): void => {
    setIsModalOpenState(open);
    if (!open) {
      setSelectedMethodIdState(null);
    }
  };

  return {
    selectedMethodId,
    setSelectedMethodId,
    isModalOpen,
    setIsModalOpen,
  };
};
